import { useCallback, useEffect, useRef } from 'react';
import { Edge, useReactFlow, useStore } from 'reactflow';

import { GraphContextType } from 'packages/tapeBuilder/ui/GraphContext';

export const useMultiConnect = (graphContext: GraphContextType) => {
  const graphContextRef = useRef<GraphContextType>(graphContext);
  const reactFlowDomNode = useStore((state) => state.domNode);

  useEffect(() => {
    // Here we are storing a new reference to the graphContext object every time
    // it changes. It would be unnecessary to update each useCallback() function
    // below every time graphContext changes, since we can just check the current
    // value of the ref whenever the user decides to copy or paste nodes. This prevents
    // some extra updates and rerenders.
    graphContextRef.current = graphContext;
  }, [graphContext]);

  const { getNodes, setEdges } = useReactFlow();

  const edgeConnect = useCallback(() => {
    const selectedNodes = getNodes().filter(
      (node) => node.selected && !node.hidden,
    );

    // Sort the nodes from leftmost node to rightmost
    const sortedNodes = selectedNodes.sort(
      (a, b) => a.position.x - b.position.x,
    );

    const newEdges: Edge[] = [];

    sortedNodes.forEach((node, i) => {
      if (i < sortedNodes.length - 1) {
        const sourceID = node.id;
        const targetID = sortedNodes[i + 1].id;

        if (
          !graphContextRef.current.state.edges.find(
            (edge) => edge.id === `${sourceID}:${targetID}`,
          )
        ) {
          // If an edge doesn't already exists for tie pair of nodes, add
          // it to the newEdges array.
          newEdges.push({
            source: sourceID,
            target: targetID,
            id: `${sourceID}:${targetID}`,
          });
        }
      }
    });

    newEdges.forEach((edge) =>
      graphContextRef.current.dispatch({ type: 'addEdge', edge }),
    );
    setEdges((edges) => [
      ...edges.map((edge) => ({ ...edge, selected: false })),
      ...newEdges,
    ]);
  }, [getNodes, setEdges]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // MetaKey is 'command' on macs
      const controlKey = navigator.userAgent.includes('Macintosh')
        ? e.metaKey
        : e.ctrlKey;

      if (controlKey && e.key === 'e') {
        edgeConnect();
      }
    }

    reactFlowDomNode?.parentElement?.addEventListener('keydown', onKeyDown);
    return () => {
      reactFlowDomNode?.parentElement?.removeEventListener(
        'keydown',
        onKeyDown,
      );
    };
  }, [reactFlowDomNode, edgeConnect]);
};
